import React, { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Users, UserPlus, X, Info } from 'lucide-react';
import { useAppSelector } from '../../store/hooks';
import Button from '../UI/Button';
import { GuestEvent } from '../../services/guestAPI';

interface BulkInviteConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  totalSelectedCount: number;
  selectedGuestEvents: GuestEvent[];
  confirmText?: string;
  cancelText?: string;
  isLoading?: boolean;
  isResolvingSelection?: boolean;
}

const BulkInviteConfirmationModal: React.FC<BulkInviteConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  totalSelectedCount,
  selectedGuestEvents,
  confirmText = 'Send Invitations',
  cancelText = 'Cancel',
  isLoading = false,
  isResolvingSelection = false
}) => {
  const { theme } = useAppSelector((state) => state.ui);

  // Calculate companion statistics from the fully resolved selection
  const companionStats = useMemo(() => {
    const guestsWithCompanions = selectedGuestEvents.filter(
      (guest) => guest.companion_count > 0
    );
    
    const totalCompanions = selectedGuestEvents.reduce(
      (sum, guest) => sum + (guest.companion_count || 0),
      0
    );

    const guestsWithoutCompanions = selectedGuestEvents.filter(
      (guest) => guest.companion_count === 0
    );

    return {
      totalGuests: totalSelectedCount,
      guestsWithCompanions: guestsWithCompanions.length,
      guestsWithoutCompanions: guestsWithoutCompanions.length,
      totalCompanions,
      guestsWithCompanionsList: guestsWithCompanions.map((g) => ({
        name: `${g.guest?.first_name || ''} ${g.guest?.last_name || ''}`.trim() || 'Unknown Guest',
        companionCount: g.companion_count || 0
      }))
    };
  }, [totalSelectedCount, selectedGuestEvents]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen && !isLoading) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, isLoading]);

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (!isLoading && e.target === e.currentTarget) {
      onClose();
    }
  };

  const modalContent = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      onClick={handleOverlayClick}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black bg-opacity-50 transition-opacity" />
      
      {/* Modal */}
      <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div
          className={`relative w-full transform overflow-hidden rounded-lg shadow-xl transition-all ${
            theme === 'dark' 
              ? 'bg-gray-900/95 border border-gray-700/50' 
              : 'bg-white border border-gray-200/50'
          }`}
        >
          {/* Header */}
          <div className={`px-6 py-5 border-b ${
            theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`flex-shrink-0 flex items-center justify-center rounded-full p-2 ${
                  theme === 'dark' ? 'bg-blue-900/30' : 'bg-blue-100'
                }`}>
                  <Users className={`h-5 w-5 ${
                    theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                  }`} />
                </div>
                <div>
                  <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    Send Invitations
                  </h3>
                  <p className={`mt-1 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    Review invitation details before sending
                  </p>
                </div>
              </div>

              {!isLoading && (
                <button
                  onClick={onClose}
                  disabled={isLoading}
                  className={`p-1.5 rounded-lg transition-colors ${
                    theme === 'dark' 
                      ? 'hover:bg-gray-700 text-gray-400 hover:text-white' 
                      : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-5 space-y-4">
            {isResolvingSelection && (
              <div className={`p-4 rounded-lg border ${
                theme === 'dark' ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'
              }`}>
                <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                  Loading details for {totalSelectedCount} selected guest{totalSelectedCount !== 1 ? 's' : ''}...
                </p>
              </div>
            )}

            {/* Summary */}
            <div className={`p-4 rounded-lg border ${
              theme === 'dark' ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'
            }`}>
              <div className="flex items-center justify-between mb-3">
                <span className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Total Guests Selected
                </span>
                <span className={`text-2xl font-bold ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  {companionStats.totalGuests}
                </span>
              </div>
              
              {companionStats.totalCompanions > 0 && (
                <div className={`mt-3 pt-3 border-t ${
                  theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                }`}>
                  <div className="flex items-center justify-between">
                    <span className={`text-sm font-medium flex items-center gap-2 ${
                      theme === 'dark' ? 'text-purple-300' : 'text-purple-700'
                    }`}>
                      <Users className="h-4 w-4" />
                      Total Companions
                    </span>
                    <span className={`text-xl font-bold ${
                      theme === 'dark' ? 'text-purple-400' : 'text-purple-600'
                    }`}>
                      +{companionStats.totalCompanions}
                    </span>
                  </div>
                  <div className={`mt-2 text-xs ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    {companionStats.guestsWithCompanions} guest{companionStats.guestsWithCompanions > 1 ? 's' : ''} {companionStats.guestsWithCompanions === 1 ? 'has' : 'have'} companion{companionStats.totalCompanions > 1 ? 's' : ''}
                  </div>
                </div>
              )}
            </div>

            {/* Companion Information */}
            {!isResolvingSelection && companionStats.guestsWithCompanions > 0 && (
              <div className={`p-4 rounded-lg border ${
                theme === 'dark' ? 'bg-purple-900/20 border-purple-700/50' : 'bg-purple-50 border-purple-200'
              }`}>
                <div className="flex items-start gap-3">
                  <Info className={`h-5 w-5 flex-shrink-0 mt-0.5 ${
                    theme === 'dark' ? 'text-purple-400' : 'text-purple-600'
                  }`} />
                  <div className="flex-1">
                    <h4 className={`font-medium mb-2 ${
                      theme === 'dark' ? 'text-purple-300' : 'text-purple-900'
                    }`}>
                      Guests with Companions
                    </h4>
                    <div className="space-y-2">
                      {companionStats.guestsWithCompanionsList.map((guest, index) => (
                        <div key={index} className={`flex items-center justify-between text-sm ${
                          theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                          <span>{guest.name}</span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            theme === 'dark'
                              ? 'bg-purple-900/30 text-purple-300'
                              : 'bg-purple-100 text-purple-700'
                          }`}>
                            {guest.companionCount} companion{guest.companionCount > 1 ? 's' : ''}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Helper Information */}
            {!isResolvingSelection && companionStats.guestsWithoutCompanions > 0 && (
              <div className={`p-4 rounded-lg border ${
                theme === 'dark' ? 'bg-blue-900/20 border-blue-700/50' : 'bg-blue-50 border-blue-200'
              }`}>
                <div className="flex items-start gap-3">
                  <UserPlus className={`h-5 w-5 flex-shrink-0 mt-0.5 ${
                    theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                  }`} />
                  <div className="flex-1">
                    <h4 className={`font-medium mb-1 ${
                      theme === 'dark' ? 'text-blue-300' : 'text-blue-900'
                    }`}>
                      Adding Companions
                    </h4>
                    <p className={`text-sm ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      {companionStats.guestsWithoutCompanions} guest{companionStats.guestsWithoutCompanions > 1 ? 's' : ''} {companionStats.guestsWithoutCompanions === 1 ? 'does' : 'do'} not have companions yet. You can add companions later by clicking on a guest card and selecting "Edit Companions".
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Main Message */}
            <div className={`p-4 rounded-lg border-2 ${
              theme === 'dark' 
                ? 'bg-yellow-900/20 border-yellow-700/50' 
                : 'bg-yellow-50 border-yellow-200'
            }`}>
              <p className={`text-sm ${
                theme === 'dark' ? 'text-yellow-300' : 'text-yellow-800'
              }`}>
                <strong>Ready to send invitations?</strong> Invitations will be sent to{' '}
                <strong>{companionStats.totalGuests} guest{companionStats.totalGuests > 1 ? 's' : ''}</strong>
                {companionStats.totalCompanions > 0 && (
                  <> along with <strong>{companionStats.totalCompanions} companion{companionStats.totalCompanions > 1 ? 's' : ''}</strong></>
                )}.
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className={`px-6 py-5 border-t flex items-center justify-end gap-3 ${
            theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
          }`}>
            <Button
              variant="secondary"
              onClick={onClose}
              disabled={isLoading}
            >
              {cancelText}
            </Button>
            <Button
              variant="primary"
              onClick={onConfirm}
              disabled={isLoading || isResolvingSelection}
              loading={isLoading}
            >
              {confirmText}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default BulkInviteConfirmationModal;

